import { GripHorizontal, Minus, X } from 'lucide-react'
import {
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'

const STORAGE_PREFIX = 'cai-widget-pos:'
const HANDLE_HEIGHT = 26
// Gap kept off every edge; the bottom keeps extra clearance for the fixed 50px
// comms bar so the widget never sits on top of it.
const EDGE_MARGIN = 16
const BOTTOM_MARGIN = 58

interface Position {
  x: number
  y: number
}

interface DraggableWidgetProps {
  /** Stable key for persisting this widget's position in localStorage. */
  name: string
  /** Content width in px (the wrapped widget's fixed width). */
  width: number
  /** Content height in px (the wrapped widget's fixed height). */
  height: number
  /** Whether the widget is shown; hidden keeps it mounted (preserves the iframe / live call). */
  visible: boolean
  zIndex?: number
  /** Hide the widget — the parent collapses it (minimize + close both call this). */
  onHide: () => void
  children: ReactNode
}

// Usable viewport, EXCLUDING scrollbars. window.innerWidth/Height include the
// scrollbar, which eats the edge margin on persistent-scrollbar setups.
function viewportWidth(): number {
  return document.documentElement.clientWidth
}
function viewportHeight(): number {
  return document.documentElement.clientHeight
}

// Keep the widget within the margins: x in [EDGE, vw-w-EDGE], y in
// [EDGE, vh-h-BOTTOM]. `w`/`h` are the box's ACTUAL rendered size (measured in
// the component, not assumed), so the margins are real regardless of borders or
// how wide the wrapped iframe renders.
function clampToViewport(pos: Position, w: number, h: number): Position {
  const maxX = Math.max(EDGE_MARGIN, viewportWidth() - w - EDGE_MARGIN)
  const maxY = Math.max(EDGE_MARGIN, viewportHeight() - h - BOTTOM_MARGIN)
  return {
    x: Math.min(Math.max(EDGE_MARGIN, pos.x), maxX),
    y: Math.min(Math.max(EDGE_MARGIN, pos.y), maxY),
  }
}

function defaultPosition(w: number, h: number): Position {
  // Bottom-right within the margins — clampToViewport pins it to the max corner.
  return clampToViewport({ x: viewportWidth(), y: viewportHeight() }, w, h)
}

function loadPosition(name: string, w: number, h: number): Position | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_PREFIX + name)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<Position>
    if (typeof parsed.x !== 'number' || typeof parsed.y !== 'number') return null
    // If the saved spot no longer fits within the margins (window shrank, etc.),
    // fall back to the default dock instead of pinning it to the edge.
    const saved = { x: parsed.x, y: parsed.y }
    const clamped = clampToViewport(saved, w, h)
    return clamped.x === saved.x && clamped.y === saved.y ? saved : null
  } catch {
    return null
  }
}

function savePosition(name: string, pos: Position): void {
  try {
    window.localStorage.setItem(STORAGE_PREFIX + name, JSON.stringify(pos))
  } catch {
    // ignore quota / disabled storage
  }
}

/**
 * Wraps a fixed-size comms widget (Aircall / Dialpad iframe) in a draggable,
 * position-remembering container. Drag it anywhere via the top handle; the
 * position persists per `name` in localStorage and is clamped to the viewport
 * so a saved spot never restores off-screen.
 *
 * Dragging works over the wrapped iframe because the handle captures the
 * pointer (`setPointerCapture`) — without it the iframe would swallow the
 * pointermove/up events mid-drag.
 */
export function DraggableWidget({
  name,
  width,
  height,
  visible,
  zIndex = 110,
  onHide,
  children,
}: DraggableWidgetProps) {
  const totalHeight = height + HANDLE_HEIGHT
  const containerRef = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState<Position>(
    () => loadPosition(name, width, totalHeight) ?? defaultPosition(width, totalHeight)
  )
  const [dragging, setDragging] = useState(false)
  const dragOriginRef = useRef<{ pointerX: number; pointerY: number; posX: number; posY: number } | null>(
    null
  )
  // Latest position for the pointerup save (avoids a stale-closure read).
  const posRef = useRef(pos)
  posRef.current = pos

  // The box's ACTUAL rendered size (falls back to props before first layout).
  // Measuring avoids off-by-border / wider-than-expected-iframe mismatches that
  // would push the default dock past the edge.
  const measure = useCallback((): { w: number; h: number } => {
    const el = containerRef.current
    return el?.offsetWidth ? { w: el.offsetWidth, h: el.offsetHeight } : { w: width, h: totalHeight }
  }, [width, totalHeight])

  // Once mounted (ref available), re-derive the position from the real size so
  // the initial dock uses true margins. Runs pre-paint, so no flicker.
  useLayoutEffect(() => {
    const { w, h } = measure()
    setPos(loadPosition(name, w, h) ?? defaultPosition(w, h))
  }, [name, measure])

  // Keep the widget within the margins when the viewport shrinks.
  useEffect(() => {
    function onResize() {
      const { w, h } = measure()
      setPos((prev) => clampToViewport(prev, w, h))
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [measure])

  const onPointerDown = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.currentTarget.setPointerCapture(e.pointerId)
    dragOriginRef.current = {
      pointerX: e.clientX,
      pointerY: e.clientY,
      posX: posRef.current.x,
      posY: posRef.current.y,
    }
    setDragging(true)
  }, [])

  const onPointerMove = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      const origin = dragOriginRef.current
      if (!origin) return
      const { w, h } = measure()
      setPos(
        clampToViewport(
          {
            x: origin.posX + (e.clientX - origin.pointerX),
            y: origin.posY + (e.clientY - origin.pointerY),
          },
          w,
          h
        )
      )
    },
    [measure]
  )

  const endDrag = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      if (!dragOriginRef.current) return
      dragOriginRef.current = null
      setDragging(false)
      if (e.currentTarget.hasPointerCapture(e.pointerId)) {
        e.currentTarget.releasePointerCapture(e.pointerId)
      }
      savePosition(name, posRef.current)
    },
    [name]
  )

  // Close: hide the widget AND forget its saved spot, so it reopens at the
  // default dock. (Minimize just hides it, keeping the position.)
  const handleClose = useCallback(() => {
    try {
      window.localStorage.removeItem(STORAGE_PREFIX + name)
    } catch {
      // ignore quota / disabled storage
    }
    const { w, h } = measure()
    setPos(defaultPosition(w, h))
    onHide()
  }, [name, measure, onHide])

  return (
    <div
      ref={containerRef}
      // Marks this as a global comms control so an open modal drawer neither
      // disables it (pointer-events) nor dismisses itself when it's clicked.
      data-comms-control
      style={{
        position: 'fixed',
        left: pos.x,
        top: pos.y,
        width,
        height: totalHeight,
        zIndex,
        pointerEvents: 'auto',
        overflow: 'hidden',
        borderRadius: 10,
        border: '1px solid var(--border)',
        background: 'white',
        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
        visibility: visible ? 'visible' : 'hidden',
        opacity: visible ? 1 : 0,
        transition: 'opacity 150ms',
      }}
    >
      {/* Whole bar drags; the grip is centered across the full width. The
          minimize/close buttons float on the right and stop pointer-down from
          starting a drag. */}
      <div
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        className="relative flex items-center justify-center bg-neutral-50 text-neutral-400 select-none"
        style={{
          height: HANDLE_HEIGHT,
          borderBottom: '1px solid var(--border)',
          cursor: dragging ? 'grabbing' : 'grab',
          touchAction: 'none',
        }}
        title="Drag to move"
        aria-label={`Move ${name} widget`}
      >
        <GripHorizontal className="size-4" />
        <div className="absolute inset-y-0 right-0 flex items-center">
          <button
            type="button"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={onHide}
            className="flex h-full cursor-pointer items-center px-2 hover:text-neutral-700"
            title="Minimize"
            aria-label={`Minimize ${name} widget`}
          >
            <Minus className="size-4" />
          </button>
          <button
            type="button"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={handleClose}
            className="flex h-full cursor-pointer items-center px-2 hover:text-neutral-700"
            title="Close and reset position"
            aria-label={`Close ${name} widget and reset its position`}
          >
            <X className="size-4" />
          </button>
        </div>
      </div>
      {/* Block iframe pointer events while dragging so it can't grab the cursor. */}
      <div style={{ width, height, pointerEvents: dragging ? 'none' : 'auto' }}>{children}</div>
    </div>
  )
}
